import React, { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Loading } from "../../components/ui";
import { AppBar } from "../../components/mobileUi";
import {
  FilterCard,
  SummaryCard,
  ListHeaderRow,
  Fab,
  StatusPill,
  CardKV,
  BORDER,
  MUTED,
  TEXT,
} from "../../components/requestUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopRequestFilters,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { getRegisterOutsideListApi } from "../../api/requests.api";
import { EMP_BASE_PATH } from "../../config";

const today = () => moment().format("YYYY-MM-DD");

const OutsideList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [sortOrder, setSortOrder] = useState("desc");

  const { data, isLoading } = useQuery({
    queryKey: ["emp", "outside-list", status, fromDate, toDate, sortOrder],
    queryFn: () =>
      getRegisterOutsideListApi({
        page: 1,
        limit: 20,
        sortBy: "createdAt",
        sortOrder,
        startTime: fromDate,
        endTime: toDate,
        ...(status !== "ALL" ? { status } : {}),
      }),
  });

  const items = data?.items || [];
  const total = data?.pagination?.total ?? data?.total ?? items.length;

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((item) => ({
      id: item._id,
      raw: item,
      createdAt: item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : "-",
      place: item.checkinPlace || "-",
      comment: item.comment || "-",
    }));

    return (
      <DesktopPage
        title="Đơn chấm công ngoài"
        right={
          <DesktopButton onClick={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai/tao`)}>
            + Đăng ký chấm công ngoài
          </DesktopButton>
        }
      >
        <DesktopStatRow items={[{ key: "total", title: "Tổng số đơn", total: total || 0 }]} />

        <DesktopPanel p="0">
          <DesktopRequestFilters
            status={status}
            onStatusChange={setStatus}
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
            sortOrder={sortOrder}
            onSortChange={setSortOrder}
          />

          <DesktopTable
            loading={isLoading}
            emptyText="Không có đơn chấm công ngoài"
            onRowClick={(row) => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai/${row.id}`)}
            rows={rows}
            headers={[
              { key: "createdAt", label: "Ngày tạo", width: "16%" },
              { key: "place", label: "Địa điểm", align: "left", width: "34%" },
              { key: "comment", label: "Lý do", align: "left", width: "34%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "16%",
                render: (row) => <StatusPill status={row.raw.status} />,
              },
            ]}
          />
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Danh sách chấm công ngoài" onBack={() => navigate(EMP_BASE_PATH)} />
      <Box px="16px" pt="12px">
      <FilterCard
        open={filterOpen}
        onToggle={() => setFilterOpen((v) => !v)}
        status={status}
        onStatusChange={setStatus}
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
      />

      <SummaryCard label="TỔNG SỐ ĐƠN" total={total} />

      <ListHeaderRow
        title="Danh sách đơn chấm công ngoài"
        sortLabel={sortOrder === "desc" ? "Mới đến cũ" : "Cũ đến mới"}
        onToggleSort={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
      />

      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
          Không có đơn chấm công ngoài
        </Text>
      ) : (
        <Flex direction="column" gap="10px">
          {items.map((item) => (
            <Box
              key={item._id}
              as="button"
              w="100%"
              textAlign="left"
              bg="white"
              border="1px solid"
              borderColor={BORDER}
              borderRadius="12px"
              p="14px"
              onClick={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai/${item._id}`)}
            >
              <Flex align="center" gap="8px" flexWrap="wrap">
                <Text fontSize="14px" color={TEXT}>
                  {moment(item.createdAt).format("DD/MM/YYYY")}
                </Text>
                <StatusPill status={item.status} />
              </Flex>
              <Box h="1px" bg={BORDER} my="12px" />
              <CardKV label="Địa điểm" value={item.checkinPlace || "-"} />
              <Box h="8px" />
              <CardKV label="Lý do" value={item.comment || "-"} />
            </Box>
          ))}
        </Flex>
      )}
      </Box>

      <Fab onClick={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai/tao`)} />
    </Box>
  );
};

export default OutsideList;
